const PORT = 48080
const HOST = '127.0.0.1'

interface ScanPayload {
  code: string
  weight: number
  weightUnit: string
  length: number
  width: number
  height: number
  measureUnit: string
  errString: string | null
}

interface NwServer {
  listen: (port: number, host: string, cb?: () => void) => void
  close: (cb?: (err?: Error | null) => void) => void
  on: (event: string, handler: (...args: any[]) => void) => void
}

interface NwScanStatus {
  running: boolean
  starting: boolean
  port: number
  host: string
  message: string
}

let _server: NwServer | null = null
let _closeServerOnUnloadRegistered = false

const scanStatus: NwScanStatus = {
  running: false,
  starting: false,
  port: PORT,
  host: HOST,
  message: '未启动',
}

let scanData: ScanPayload | null = null

/** 解析扫码原始串：code,weight,length,width,height（末尾可有等号） */
function parseScanPayload(raw: string): ScanPayload | null {
  if (!raw || typeof raw !== 'string')
    return null
  const str = raw.trim().replace(/=+$/, '')
  const parts = str.split(',')
  if (parts.length < 5)
    return null
  const [code, weight, length, width, height] = parts
  const w = Number(weight)
  const l = Number(length)
  const wi = Number(width)
  const h = Number(height)
  if (Number.isNaN(w) || Number.isNaN(l) || Number.isNaN(wi) || Number.isNaN(h))
    return null

  return {
    code: code.trim(),
    weight: w,
    weightUnit: 'kg',
    length: l,
    width: wi,
    height: h,
    measureUnit: 'cm',
    errString: null,
  }
}

/**
 * 从 POST body 中取出扫码串（兼容 Java 端 getParameterNames() 取首个 param 值的方式）
 * 支持：1）application/x-www-form-urlencoded 的 params（取第一个参数的值）
 *      2）纯文本 body：code,weight,length,width,height 或 key=上述串
 */
function readPostBody(
  req: any,
  BufferClass: { concat: (list: Uint8Array[]) => Uint8Array & { toString: (enc?: string) => string } },
): Promise<string> {
  return new Promise((resolve, reject) => {
    const chunks: Uint8Array[] = []
    req.on('data', (chunk: Uint8Array) => chunks.push(chunk))
    req.on('end', () => {
      const raw = BufferClass.concat(chunks).toString('utf8')
      try {
        const decoded = raw.includes('%') ? decodeURIComponent(raw) : raw
        const trimmed = decoded.trim()

        if (trimmed.includes('=') && trimmed.includes('&')) {
          const firstParam = trimmed.split('&')[0]
          const eq = firstParam.indexOf('=')
          if (eq !== -1) {
            resolve(firstParam.slice(eq + 1).trim())
            return
          }
        }
        if (trimmed.includes('=')) {
          const firstEq = trimmed.indexOf('=')
          if (firstEq < trimmed.length - 1) {
            resolve(trimmed.slice(firstEq + 1).trim())
            return
          }
        }

        resolve(trimmed)
      }
      catch {
        resolve(raw.trim())
      }
    })
    req.on('error', reject)
  })
}

function setScanStatus(partial: Partial<NwScanStatus>) {
  Object.assign(scanStatus, partial)
}

function createScanServer(): NwServer {
  const http = window.require('http')
  const BufferClass = window.require('buffer').Buffer

  return http.createServer(async (req, res) => {
    let pathname = '/'
    let search = ''
    try {
      const u = new URL(req.url || '', `http://${HOST}:${PORT}`)
      pathname = u.pathname.replace(/\/$/, '') || '/'
      search = u.search ? decodeURIComponent(u.search.substring(1)) : ''
    }
    catch {
      // 兼容畸形 URL
    }

    const allowCors = () => {
      res.writeHead(200, {
        'Content-Type': 'application/json; charset=utf-8',
        'Access-Control-Allow-Origin': '*',
      })
      res.end('OK')
    }

    if (pathname === '/Post' && req.method === 'GET') {
      const rawParams = search
      console.warn('[nwScan] GET /Post params:', rawParams)
      if (rawParams) {
        const parsed = parseScanPayload(rawParams)
        if (parsed) {
          scanData = parsed
          console.warn('扫描数据:', scanData)
        }
      }
      allowCors()
      return
    }

    if (pathname === '/Post' && req.method === 'POST') {
      const body = await readPostBody(req, BufferClass)
      console.warn('[nwScan] POST /Post body:', body)
      if (body) {
        const parsed = parseScanPayload(body)
        if (parsed) {
          scanData = parsed
          console.warn('扫描数据:', scanData)
        }
      }
      else {
        const rawParams = search
        console.warn('[nwScan] POST /Post params:', rawParams)
        if (rawParams) {
          const parsed = parseScanPayload(rawParams)
          if (parsed) {
            scanData = parsed
            console.warn('扫描数据:', scanData)
          }
        }
      }
      allowCors()
      return
    }

    if (pathname === '/saveScanData' && req.method === 'POST') {
      const body = await readPostBody(req, BufferClass)
      console.warn('[nwScan] POST /saveScanData body:', body)
      if (body) {
        const parsed = parseScanPayload(body)
        if (parsed) {
          scanData = parsed
          console.warn('扫描数据:', scanData)
        }
      }
      else {
        const rawParams = search
        console.warn('[nwScan] POST /saveScanData params:', rawParams)
        if (rawParams) {
          const parsed = parseScanPayload(rawParams)
          if (parsed) {
            scanData = parsed
            console.warn('扫描数据:', scanData)
          }
        }
      }
      allowCors()
      return
    }

    res.writeHead(404)
    res.end('Not Found')
  })
}

function closeServerInstance(server: NwServer | null): Promise<void> {
  return new Promise((resolve) => {
    if (!server) {
      resolve()
      return
    }

    try {
      server.close(() => {
        resolve()
      })
    }
    catch {
      resolve()
    }
  })
}

function execCommand(command: string): Promise<{ stdout: string, stderr: string }> {
  return new Promise((resolve, reject) => {
    const { exec } = window.require('child_process')
    exec(command, (error: Error | null, stdout: string, stderr: string) => {
      if (error) {
        reject(new Error(stderr || error.message))
        return
      }
      resolve({ stdout, stderr })
    })
  })
}

function parsePidList(netstatOutput: string): number[] {
  const lines = netstatOutput
    .split(/\r?\n/)
    .map(line => line.trim())
    .filter(Boolean)

  const pidSet = new Set<number>()
  lines.forEach((line) => {
    if (!line.includes(`:${PORT}`))
      return
    const tokens = line.split(/\s+/)
    const pid = Number(tokens[tokens.length - 1])
    if (!Number.isNaN(pid) && pid > 0)
      pidSet.add(pid)
  })

  return Array.from(pidSet)
}

async function killPortOccupyingProcesses(): Promise<{ success: boolean, message: string }> {
  if (!window.require) {
    return { success: false, message: '当前环境不支持端口清理' }
  }

  if (typeof process !== 'object' || process.platform !== 'win32') {
    return { success: false, message: '当前平台暂不支持自动清理端口' }
  }

  try {
    const { stdout } = await execCommand(`netstat -ano | findstr :${PORT}`)
    const pidList = parsePidList(stdout)
    if (pidList.length === 0) {
      return { success: true, message: `未发现占用 ${PORT} 端口的进程` }
    }

    await Promise.all(pidList.map(pid => execCommand(`taskkill /F /PID ${pid}`)))
    return {
      success: true,
      message: `已强制关闭占用 ${PORT} 端口的进程: ${pidList.join(', ')}`,
    }
  }
  catch (error: any) {
    const errorMessage = error?.message || '端口清理失败'
    if (String(errorMessage).includes('findstr')) {
      return { success: true, message: `未发现占用 ${PORT} 端口的进程` }
    }
    return { success: false, message: errorMessage }
  }
}

function ensureBeforeUnloadHandler() {
  if (_closeServerOnUnloadRegistered)
    return

  const closeServerOnUnload = () => {
    void stopNwScan()
  }

  window.addEventListener('beforeunload', closeServerOnUnload)
  _closeServerOnUnloadRegistered = true
}

export async function startNwScan(): Promise<{ success: boolean, message: string }> {
  ensureBeforeUnloadHandler()

  if (!window.require) {
    setScanStatus({
      running: false,
      starting: false,
      message: '当前环境不支持扫码平台',
    })
    return { success: false, message: scanStatus.message }
  }

  if (scanStatus.running && _server) {
    return { success: true, message: '扫码平台已启动' }
  }

  if (scanStatus.starting) {
    return { success: false, message: '扫码平台启动中，请稍候' }
  }

  setScanStatus({
    running: false,
    starting: true,
    message: '启动中',
  })

  return await new Promise((resolve) => {
    const server = createScanServer()
    let settled = false

    const finish = (result: { success: boolean, message: string }) => {
      if (settled)
        return
      settled = true
      resolve(result)
    }

    server.on('error', (error: any) => {
      const errorCode = error?.code
      const message = errorCode === 'EADDRINUSE'
        ? `启动失败，端口 ${PORT} 被占用`
        : `启动失败: ${error?.message || '未知错误'}`

      _server = null
      setScanStatus({
        running: false,
        starting: false,
        message,
      })
      console.error('[nwScan] 启动失败:', error)
      finish({ success: false, message })
    })

    server.listen(PORT, HOST, () => {
      _server = server
      setScanStatus({
        running: true,
        starting: false,
        message: '已启动',
      })
      console.warn(`扫码平台服务已启动，正在监听接口: http://${HOST}:${PORT}`)
      finish({ success: true, message: '扫码平台已启动' })
    })
  })
}

export async function stopNwScan(options?: { forceKillPort?: boolean }): Promise<{ success: boolean, message: string }> {
  const shouldForceKillPort = Boolean(options?.forceKillPort)
  const currentServer = _server

  _server = null
  setScanStatus({
    running: false,
    starting: false,
    message: '未启动',
  })

  await closeServerInstance(currentServer)

  let message = currentServer ? '扫码平台已关闭' : '扫码平台未启动'
  let success = true

  if (shouldForceKillPort) {
    const killResult = await killPortOccupyingProcesses()
    success = success && killResult.success
    message = `${message}；${killResult.message}`

    if (!killResult.success) {
      setScanStatus({
        running: false,
        starting: false,
        message: `未启动，${killResult.message}`,
      })
      return { success: false, message }
    }
  }

  setScanStatus({
    running: false,
    starting: false,
    message: '未启动',
  })
  console.warn('[nwScan] 扫码服务已关闭')
  return { success, message }
}

export function initNwScan() {
  ensureBeforeUnloadHandler()

  if (!window.require) {
    setScanStatus({
      running: false,
      starting: false,
      message: '当前环境不支持扫码平台',
    })
    return
  }

  void startNwScan().then((result) => {
    if (!result.success)
      console.warn('[nwScan]', result.message)
  })
}

export function getNwScanStatus(): NwScanStatus {
  return {
    ...scanStatus,
  }
}

export function getScanData() {
  return scanData
}

export function resetScanData() {
  scanData = null
}
